/**
 * Field Sync Rules — for one connector, each Item field's direction, source and,
 * for a field that syncs both ways, what happens when both sides changed it
 * (canon 01 › Field Source, Sync Direction and Conflict Rule). Saved to
 * connector_configs.trust_rules; read by the item page, the item link sync,
 * and the `dispatch` and `apply` functions through one module
 * (supabase/functions/_shared/fieldSyncRules.ts).
 *
 * Every Item field has a row. A field this connector cannot carry yet says so
 * instead of being left out. The direction decides the source where it can (into
 * Priorities.ai → the other system; out of it or not synced → Priorities.ai), so
 * a contradicting pair cannot be chosen; only "both ways" asks for a source.
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Bot, Loader2, Save } from 'lucide-react';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { suggestTrustRules, type TrustRuleAdvisorResult } from '../../../lib/llmSyncAdvisorService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import { INTERNAL_FIELDS, parseFieldMappingConfig } from '../../../lib/fieldMappingRegistry';
import { attributeKeyOfTarget, resolveActiveMappingSet } from '../../../lib/fieldMappingApply';
import { INBOUND_TRANSPORT, OUTBOUND_TRANSPORT } from '../../../lib/semanticConnectors/unifiedAttributes';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import {
  CONFLICT_WORDS,
  DEFAULT_FIELD_RULES,
  DIRECTION_WORDS,
  fieldSyncRuleFor,
  fieldSyncRuleProblem,
  readStoredRule,
  ruleFromMappingDirection,
  storedRuleOf,
  type ConflictRule,
  type FieldSyncDirection,
  type FieldSyncRule,
  type StoredFieldRules,
} from '../../../../supabase/functions/_shared/fieldSyncRules';
import { ENGINE_WRITABLE_FIELDS } from '../../../../supabase/functions/_shared/fieldSyncOutbound';

const SYSTEM_NAMES: Record<string, string> = { jira: 'Jira', linear: 'Linear', notion: 'Notion', miro: 'Miro', slack: 'Slack' };

/** Linear reports one change time for a whole issue, not one per field. */
const NO_PER_FIELD_TIMES = new Set(['linear']);

/** Fields with built-in defaults that are not in the field registry under the same key. */
const EXTRA_FIELD_LABELS: Record<string, string> = {
  priority_rank: 'Priority rank',
  scope: 'Scope',
  cycle_id: 'Cycle',
  assignee: 'Assignee',
  time_spent: 'Time spent',
  due_date: 'Due date',
  labels: 'Labels',
  tags: 'Tags',
};

interface Row {
  field: string;
  label: string;
  carriedIn: boolean;
  carriedOut: boolean;
  /** The direction the Field Mappings rule gives an attribute with no rule of its own. */
  fromMapping: FieldSyncRule | null;
}

interface Props {
  connectorId: string;
  connectorType: string;
  currentRules: StoredFieldRules;
  fieldMappings: unknown;
  connectorDirection: 'inbound' | 'outbound' | 'both';
  onSaved: (rules: StoredFieldRules) => void;
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** The advisor speaks in trust_rules' stored words; give it the source of each saved rule. */
function asOwnerWords(rules: StoredFieldRules): Record<string, 'priorities_ai' | 'external' | 'negotiated'> {
  const out: Record<string, 'priorities_ai' | 'external' | 'negotiated'> = {};
  for (const [field, stored] of Object.entries(rules)) {
    const r = readStoredRule(stored);
    if (!r) continue;
    out[field] = r.direction === 'both' && r.conflict === 'person_decides' ? 'negotiated' : r.owner;
  }
  return out;
}

export function FieldSyncRulesEditor({ connectorId, connectorType, currentRules, fieldMappings, connectorDirection, onSaved }: Props) {
  const system = SYSTEM_NAMES[connectorType] ?? humanize(connectorType);
  const attributes = useAttributesStore((s) => s.attributes);
  const syncAdvisorEnabled = useAiCapability('sync_advisor');
  const [rules, setRules] = useState<StoredFieldRules>(currentRules ?? {});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<TrustRuleAdvisorResult | null>(null);
  const [showUncarried, setShowUncarried] = useState(false);

  const rows = useMemo((): Row[] => {
    const inbound = INBOUND_TRANSPORT[connectorType] ?? new Set<string>();
    const outbound = OUTBOUND_TRANSPORT[connectorType] ?? new Set<string>();
    const writable = ENGINE_WRITABLE_FIELDS[connectorType] ?? new Set<string>();
    const out = new Map<string, Row>();
    const add = (field: string, label: string, carriedIn: boolean, carriedOut: boolean, fromMapping: FieldSyncRule | null = null) => {
      const had = out.get(field);
      out.set(field, {
        field,
        label: had?.label ?? label,
        carriedIn: carriedIn || !!had?.carriedIn,
        carriedOut: carriedOut || !!had?.carriedOut,
        fromMapping: fromMapping ?? had?.fromMapping ?? null,
      });
    };

    // Attributes reach this connector through its Field Mappings rules.
    const { set } = resolveActiveMappingSet(parseFieldMappingConfig(fieldMappings as never), 'import');
    for (const entry of set?.entries ?? []) {
      if (!entry.enabled || !entry.externalFieldKey) continue;
      const key = attributeKeyOfTarget(entry.internalFieldKey);
      if (key) {
        const label = attributes.find((a) => a.key === key)?.label ?? humanize(key);
        add(`attribute:${key}`, label, true, writable.has(entry.externalFieldKey), ruleFromMappingDirection(entry.direction));
      } else if (!entry.internalFieldKey.includes(':')) {
        const def = INTERNAL_FIELDS.find((f) => f.key === entry.internalFieldKey);
        add(entry.internalFieldKey, def?.label ?? humanize(entry.internalFieldKey), inbound.has(entry.internalFieldKey), outbound.has(entry.internalFieldKey), ruleFromMappingDirection(entry.direction));
      }
    }
    for (const field of ['title', 'description', 'tags']) {
      add(field, INTERNAL_FIELDS.find((f) => f.key === field)?.label ?? EXTRA_FIELD_LABELS[field], inbound.has(field), outbound.has(field));
    }
    for (const field of Object.keys(DEFAULT_FIELD_RULES)) {
      if (field === 'labels') continue; // Jira's name for tags
      add(field, EXTRA_FIELD_LABELS[field] ?? INTERNAL_FIELDS.find((f) => f.key === field)?.label ?? humanize(field), false, false);
    }
    for (const def of INTERNAL_FIELDS) add(def.key, def.label, false, false);
    for (const field of Object.keys(currentRules ?? {})) {
      if (field === '*' || field === 'summary' || field === 'labels') continue;
      add(field, EXTRA_FIELD_LABELS[field] ?? humanize(field.replace(/^attribute:/, '')), false, false);
    }
    return [...out.values()];
  }, [attributes, connectorType, currentRules, fieldMappings]);

  const carried = rows.filter((r) => r.carriedIn || r.carriedOut);
  const uncarried = rows.filter((r) => !r.carriedIn && !r.carriedOut);

  const ruleOf = (row: Row): FieldSyncRule => {
    // Jira's own names for title and tags, from rules saved before 2026-10-28.
    const alias = row.field === 'title' ? 'summary' : row.field === 'tags' ? 'labels' : null;
    const saved = readStoredRule(rules[row.field]) ?? (alias ? readStoredRule(rules[alias]) : null);
    return saved ?? fieldSyncRuleFor(rules, row.field, row.fromMapping);
  };

  const setRule = (row: Row, next: FieldSyncRule) => {
    setRules((prev) => {
      const copy = { ...prev, [row.field]: storedRuleOf(next) };
      if (row.field === 'title') delete copy.summary;
      if (row.field === 'tags') delete copy.labels;
      return copy;
    });
  };

  const chooseDirection = (row: Row, direction: FieldSyncDirection) => {
    const now = ruleOf(row);
    const owner = direction === 'in' ? 'external' : direction === 'both' ? now.owner : 'priorities_ai';
    setRule(row, { owner, direction, conflict: direction === 'both' ? now.conflict : 'owner_wins' });
  };

  const problems = carried.map((r) => fieldSyncRuleProblem(ruleOf(r))).filter(Boolean) as string[];

  async function handleSave() {
    const workspaceId = getCurrentWorkspaceId();
    const supabase = getSafeSupabaseClient();
    if (!workspaceId || !supabase || problems.length > 0) return;
    setIsSaving(true);
    setSaveError(null);
    const { error } = await supabase
      .from('connector_configs')
      .update({ trust_rules: rules })
      .eq('id', connectorId)
      .eq('workspace_id', workspaceId);
    setIsSaving(false);
    if (error) setSaveError(error.message);
    else onSaved(rules);
  }

  async function handleAiSuggest() {
    setIsAiLoading(true);
    setAiResult(null);
    setAiResult(await suggestTrustRules({ connectorType, currentRules: asOwnerWords(rules) }));
    setIsAiLoading(false);
  }

  const directionOptions = (row: Row): FieldSyncDirection[] =>
    (['none', 'in', 'out', 'both'] as FieldSyncDirection[]).filter(
      (d) => d === 'none' || (d === 'in' && row.carriedIn) || (d === 'out' && row.carriedOut) || (d === 'both' && (row.carriedIn || row.carriedOut)),
    );

  const selectClass =
    'text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] px-2 py-1';

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs text-[var(--text-ter)]">
          For each field: which way it moves between Priorities.ai and {system}, which system’s value is the item’s value, and, for a
          field that moves both ways, what happens when both sides changed it. The rules reach an item through its link to {system}.
          {connectorDirection !== 'both' && (
            <> This connection is set to {connectorDirection === 'inbound' ? `receive only, so nothing goes to ${system}` : `send only, so nothing comes in from ${system}`}.</>
          )}
        </p>
        {syncAdvisorEnabled && (
          <button
            onClick={handleAiSuggest}
            disabled={isAiLoading}
            className="flex shrink-0 items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)] disabled:opacity-50"
          >
            {isAiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bot className="h-3 w-3" />}
            Suggest sources
          </button>
        )}
      </div>

      {aiResult && (
        <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)] space-y-2">
          <p className="text-xs text-[var(--text-sec)]">{aiResult.rationale}</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(aiResult.suggestedRules).map(([field, owner]) => (
              <span key={field} className="text-xs bg-[var(--hover-tint)] px-2 py-0.5">
                {rows.find((r) => r.field === field)?.label ?? field}: {DIRECTION_WORDS[readStoredRule(owner)?.direction ?? 'none']}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setRules((prev) => ({ ...prev, ...aiResult.suggestedRules }));
                setAiResult(null);
              }}
              className="text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)]"
            >
              Use these
            </button>
            <button onClick={() => setAiResult(null)} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto border border-[var(--rule)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--surface)] border-b border-[var(--rule)] text-left">
              <th className="px-3 py-2 text-xs font-semibold text-[var(--text-ter)]">Field</th>
              <th className="px-3 py-2 text-xs font-semibold text-[var(--text-ter)]">Moves</th>
              <th className="px-3 py-2 text-xs font-semibold text-[var(--text-ter)]">Source</th>
              <th className="px-3 py-2 text-xs font-semibold text-[var(--text-ter)]">When both sides changed it</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)]">
            {carried.map((row) => {
              const rule = ruleOf(row);
              const problem = fieldSyncRuleProblem(rule);
              return (
                <tr key={row.field}>
                  <td className="px-3 py-2 text-xs text-[var(--text-pri)]">{row.label}</td>
                  <td className="px-3 py-2">
                    <select
                      aria-label={`${row.label}: which way it moves`}
                      className={selectClass}
                      value={rule.direction}
                      onChange={(e) => chooseDirection(row, e.target.value as FieldSyncDirection)}
                    >
                      {directionOptions(row).map((d) => (
                        <option key={d} value={d}>{DIRECTION_WORDS[d]}</option>
                      ))}
                      {!directionOptions(row).includes(rule.direction) && (
                        <option value={rule.direction}>{DIRECTION_WORDS[rule.direction]} (not carried by {system} yet)</option>
                      )}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--text-sec)]">
                    {rule.direction === 'both' ? (
                      <select
                        aria-label={`${row.label}: source`}
                        className={selectClass}
                        value={rule.owner}
                        onChange={(e) => setRule(row, { ...rule, owner: e.target.value as FieldSyncRule['owner'] })}
                      >
                        <option value="priorities_ai">Priorities.ai</option>
                        <option value="external">{system}</option>
                      </select>
                    ) : (
                      rule.owner === 'external' ? system : 'Priorities.ai'
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--text-sec)]">
                    {rule.direction === 'both' ? (
                      <div className="space-y-1">
                        <select
                          aria-label={`${row.label}: when both sides changed it`}
                          className={selectClass}
                          value={rule.conflict}
                          onChange={(e) => setRule(row, { ...rule, conflict: e.target.value as ConflictRule })}
                        >
                          <option value="owner_wins">{CONFLICT_WORDS.owner_wins}</option>
                          <option value="most_recent_wins">{CONFLICT_WORDS.most_recent_wins} (low-stakes fields only)</option>
                          <option value="person_decides">{CONFLICT_WORDS.person_decides}</option>
                        </select>
                        {rule.conflict === 'most_recent_wins' && NO_PER_FIELD_TIMES.has(connectorType) && (
                          <p className="text-[11px] text-[var(--text-ter)]">
                            {system} does not say when each field changed, so this acts as Source wins.
                          </p>
                        )}
                        {rule.conflict === 'person_decides' && (
                          <p className="text-[11px] text-[var(--text-ter)]">Goes to the Conflict Queue; the source’s value stands until then.</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-[var(--text-ter)]">
                        {rule.direction === 'none' ? '—' : `A change on the other side is kept as a difference`}
                      </span>
                    )}
                    {problem && <p className="mt-1 text-[11px] text-[var(--coral)]">{problem}</p>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[var(--text-ter)]">
        Whatever the rule, the value that did not win is kept in the item’s History.{' '}
        <button className="underline hover:text-[var(--text-sec)]" onClick={() => setShowUncarried((v) => !v)}>
          {showUncarried ? 'Hide' : 'Show'} the {uncarried.length} fields {system} does not carry yet
        </button>
      </p>
      {showUncarried && (
        <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-[var(--text-ter)] sm:grid-cols-3">
          {uncarried.map((r) => (
            <li key={r.field}>{r.label} — not carried by {system} yet</li>
          ))}
        </ul>
      )}

      {saveError && (
        <p className="text-xs text-[var(--coral)] flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {saveError}
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        {problems.length > 0 && <p className="text-xs text-[var(--coral)]">{problems[0]}</p>}
        <button
          onClick={handleSave}
          disabled={isSaving || problems.length > 0}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--slate)] disabled:opacity-50 text-white"
        >
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save field sync rules
        </button>
      </div>
    </div>
  );
}
